"use client";

import { useEffect, useMemo, useState } from "react";
import styles from "./VariantBlock.module.scss";
import Text from "@components/atoms/Text";
import { SliderInput } from "../SliderInput";
import { calculateFuelCost } from "@helpers/fuelCostCalculatorHelper";
import Image from "next/image";
import assetLoader from "@utils/internalImageLoder";
import { Modal } from "@components/atoms/Modal";
import { VariantBlockProps, VariantDetail } from "./VariantBlock.types";
import { useIsMobile } from "@hooks/use-mobile";
import { trackEventWithCommonProps, EventType } from "@utils/eventTracker";
import { PAGE_TYPES } from "@constants/pageTypes";
import { getCarAvailability } from "@utils/carAvailability";

export const VariantBlock = ({
  variants,
  updateVariantDistance,
  getVariantDistance,
  generateVariantKey,
  modelName,
  makeName,
  isSellable,
  cityName,
  carAvailability
}: VariantBlockProps) => {
  const [showAll, setShowAll] = useState(false);
  const [customDistance, setCustomDistance] = useState(40);
  const [editingVariant, setEditingVariant] = useState<VariantDetail | null>(
    null
  );

  const isMobile = useIsMobile();

  useEffect(() => {
    setShowAll(false);
  }, [variants]);

  useEffect(() => {
    if (editingVariant !== null) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [editingVariant]);

  const displayLimit = useMemo(() => {
    return isMobile ? 4 : 9;
  }, [isMobile]);

  const visibleVariants = useMemo(() => {
    return showAll ? variants : variants.slice(0, displayLimit);
  }, [showAll, variants, displayLimit]);

  const hiddenCount = variants.length - visibleVariants.length;

  const getButtonText = (count: number, isViewMore: boolean) => {
    if (!isViewMore) {
      return "View less variants";
    }
    return `View ${count} more variants`;
  };

  const handleEditClick = async (variant: VariantDetail) => {
    const currentDistance = getVariantDistance(variant);
    setEditingVariant(variant);
    setCustomDistance(currentDistance);
    
    await trackEventWithCommonProps("action", EventType.TRACK_EVENT, {
      pageType: PAGE_TYPES.MILEAGE_PAGE,
      additionalProps: {
        action_type: "tap_btn",
        action_name: "tap_btn_edit_distance",
        section: "Variant fuel cost",
        car_availability: carAvailability || getCarAvailability(`${isSellable}`),
        city_name: cityName,
        vehicle_make: makeName,
        vehicle_model: modelName,
      },
    });
  };

  const handleCalculateClick = () => {
    if (editingVariant) {
      updateVariantDistance(editingVariant, customDistance);
      setEditingVariant(null);
    }
  };

  return (
    <div className={styles.variantBlock}>
      <div className={styles.grid}>
        {visibleVariants.map((variant: VariantDetail, index: number) => {
          const stableKey = `${generateVariantKey(variant)}-idx-${index}`;
          const distance = getVariantDistance(variant);

          return (
            <div key={stableKey} className={styles.variantCard}>
              <div className={styles.variantInfo}>
                <div className={styles.variantHeader}>
                  <Text className={styles.variantTitle}>{variant.name}</Text>
                  <Text className={styles.variantMileage}>
                    {`${variant.mileage} ${variant.unit}`}
                  </Text>
                </div>
                <div className={styles.variantDetails}>
                  <Text className={styles.variantMeta}>
                    {`${variant.fuelType} • ${variant.transmission}`}
                  </Text>
                  <Text className={styles.variantMeta}>
                    {variant.mileageType}
                  </Text>
                </div>
              </div>
              <div className={styles.fuelCost}>
                <div className={styles.fuelCostText}>
                  <Image
                    src={`${assetLoader()}/icons/IndianRupee.svg`}
                    alt="Indian_Rupee"
                    width={20}
                    height={20}
                    loading="lazy"
                  />
                  <Text className={styles.fuelLabel}>
                    {`Monthly Fuel cost for ${distance}km/day`}
                    <span className={styles.fuelValue}>
                      {`₹${calculateFuelCost(variant, distance)}`}
                    </span>
                  </Text>
                </div>
                <Image
                  src={`${assetLoader()}/icons/Edit_Pencil.svg`}
                  alt="Edit_Pencil"
                  width={20}
                  height={20}
                  className={styles.editIcon}
                  onClick={() => handleEditClick(variant)}
                  loading="lazy"
                />
              </div>
            </div>
          );
        })}
      </div>

      {!showAll && hiddenCount > 0 && (
        <button className={styles.viewMoreBtn} onClick={() => setShowAll(true)}>
          {getButtonText(hiddenCount, true)}
          <Image
            src={`${assetLoader()}/icons/Chevron_down.svg`}
            alt="Chevron_down"
            width={22}
            height={22}
            loading="lazy"
          />
        </button>
      )}

      {showAll && variants.length > displayLimit && (
        <button
          className={styles.viewMoreBtn}
          onClick={() => setShowAll(false)}
        >
          {getButtonText(0, false)}
          <Image
            src={`${assetLoader()}/icons/Chevron_up.svg`}
            alt="Chevron_up"
            width={22}
            height={22}
            loading="lazy"
          />
        </button>
      )}

      {editingVariant !== null && (
        <Modal
          isOpen={true}
          onClose={() => setEditingVariant(null)}
          overlayClassName={styles.modalOverlay}
          contentClassName={styles.modalContent}
          bodyClassName={styles.modalBody}
          headerClassName={styles.modalHeader}
          title="Change daily driven distance"
        >
          <Text className={styles.modalDescription}>
            Choose your daily driven kilometre and calculate your monthly fuel
            cost prices
          </Text>
          <SliderInput
            label=""
            value={customDistance}
            min={20}
            max={200}
            step={1}
            suffix=" km"
            onChange={(e: any) => setCustomDistance(Number(e))}
          />
          <button
            className={styles.calculateBtn}
            onClick={handleCalculateClick}
          >
            Calculate
          </button>
        </Modal>
      )}
    </div>
  );
};
